<template>
  <v-stepper-window-item value="1">
    <v-card-text>
      <!-- Profile Selection Table -->
      <v-card
        title="Export Profiles"
        subtitle="Select one or more profiles to export to Excel for editing"
        variant="outlined"
        class="mb-4"
      >
        <v-card-text>
          <v-data-table
            v-model="selectedProfiles"
            :items="profiles"
            :headers="tableHeaders"
            show-select
            :loading="loading"
            density="compact"
            item-value="code"
            return-object
          >
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template #item.profile.isPublished="{ value }">
              <v-chip :color="value ? 'success' : 'default'" size="small">
                {{ value ? 'Published' : 'Draft' }}
              </v-chip>
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template #item.taskCount="{ item }">
              {{ item.profile?.taskProfiles?.length || 0 }}
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <!-- Export Options -->
      <v-expansion-panels v-if="selectedProfiles.length > 0">
        <v-expansion-panel>
          <v-expansion-panel-title>
            <div class="d-flex flex-column">
              <div class="text-h6">Export Options</div>
            </div>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <v-alert
              text="Choose which fields to include as columns."
              type="info"
              variant="outlined"
              density="compact"
            ></v-alert>

            <v-row class="mt-2">
              <v-col cols="12" class="py-0">
                <v-list
                  class="mx-auto ga-4 d-flex flex-column"
                  color="primary"
                  lines="two"
                >
                  <!-- Task Codes Option -->
                  <v-list-item rounded="lg" class="mb-2" :ripple="false">
                    <template #prepend>
                      <v-checkbox
                        v-model="includeTaskCodes"
                        density="compact"
                        color="primary"
                        hide-details
                        @click.stop
                      ></v-checkbox>
                    </template>
                    <template #title>
                      <div class="d-flex justify-space-between align-center text-body-2">
                        <strong>Task Codes</strong>
                      </div>
                    </template>
                    <template #subtitle>
                      <div class="text-medium-emphasis">
                        Include task codes column in spreadsheet
                      </div>
                    </template>
                  </v-list-item>

                  <!-- Lead Team Option -->
                  <v-list-item rounded="lg" class="mb-2" :ripple="false">
                    <template #prepend>
                      <v-checkbox
                        v-model="includeLeadTeam"
                        density="compact"
                        color="primary"
                        hide-details
                        @click.stop
                      ></v-checkbox>
                    </template>
                    <template #title>
                      <div class="d-flex justify-space-between align-center text-body-2">
                        <strong>Lead Team</strong>
                      </div>
                    </template>
                    <template #subtitle>
                      <div class="text-medium-emphasis">
                        Include lead team column in spreadsheet
                      </div>
                    </template>
                  </v-list-item>

                  <!-- Weighting Option -->
                  <v-list-item rounded="lg" class="mb-2" :ripple="false">
                    <template #prepend>
                      <v-checkbox
                        v-model="includeWeighting"
                        density="compact"
                        color="primary"
                        hide-details
                        @click.stop
                      ></v-checkbox>
                    </template>
                    <template #title>
                      <div class="d-flex justify-space-between align-center text-body-2">
                        <strong>Weighting</strong>
                      </div>
                    </template>
                    <template #subtitle>
                      <div class="text-medium-emphasis">
                        Include weighting column in spreadsheet
                      </div>
                    </template>
                  </v-list-item>
                </v-list>
              </v-col>
            </v-row>

            <!-- Export Button -->
            <div class="d-flex justify-start mt-4">
              <v-btn
                prepend-icon="mdi-file-excel"
                color="success"
                :disabled="selectedProfiles.length === 0 || loading"
                :loading="exporting"
                @click="handleExport"
              >
                Export Selected ({{ selectedProfiles.length }})
              </v-btn>
            </div>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card-text>

    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn
        color="primary"
        @click="$emit('continue')"
      >
        Continue to Upload
      </v-btn>
    </v-card-actions>
  </v-stepper-window-item>
</template>

<script setup>
import { ref, computed } from 'vue';

defineProps({
  profiles: {
    type: Array,
    required: true
  },
  loading: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['continue', 'export']);

// State
const selectedProfiles = ref([]);
const exporting = ref(false);
const includeTaskCodes = ref(true);
const includeLeadTeam = ref(true);
const includeWeighting = ref(true);

// Table headers
const tableHeaders = [
  { title: 'Code', key: 'code', width: '120px' },
  { title: 'Title', key: 'profile.title' },
  { title: 'Status', key: 'profile.isPublished', width: '100px' },
  { title: 'Tasks', key: 'taskCount', width: '80px' }
];

// Calculate max depth of task hierarchy for selected profiles
function calculateTaskDepth(taskProfiles, currentDepth = 0) {
  if (!taskProfiles || taskProfiles.length === 0) return currentDepth;
  let maxDepth = currentDepth;
  for (const task of taskProfiles) {
    if (task.children?.length > 0) {
      const childDepth = calculateTaskDepth(task.children, currentDepth + 1);
      maxDepth = Math.max(maxDepth, childDepth);
    }
  }
  return maxDepth;
}

const computedMinTaskDepth = computed(() => {
  if (selectedProfiles.value.length === 0) return 1;
  let maxDepth = 0;
  for (const selected of selectedProfiles.value) {
    const profile = selected.profile;
    if (profile?.taskProfiles) {
      const depth = calculateTaskDepth(profile.taskProfiles) + 1; // +1 because depth is 0-indexed
      maxDepth = Math.max(maxDepth, depth);
    }
  }
  return Math.max(maxDepth, 1);
});

async function handleExport() {
  if (selectedProfiles.value.length === 0) return;

  exporting.value = true;
  try {
    const exportOptions = {
      taskLevels: computedMinTaskDepth.value,
      includeTaskCodes: includeTaskCodes.value,
      includeLeadTeam: includeLeadTeam.value,
      includeWeighting: includeWeighting.value
    };
    const profilesToExport = selectedProfiles.value.map(sp => sp.profile);
    emit('export', { profiles: profilesToExport, options: exportOptions });
  } finally {
    exporting.value = false;
  }
}
</script>

<style scoped>
.v-list-item--active {
  background-color: transparent !important;
}

.v-list-item--active:hover {
  background-color: rgba(var(--v-theme-primary), 0.05) !important;
}

.v-list-item:hover {
  background-color: rgba(var(--v-theme-primary), 0.05) !important;
}
</style>
